import * as React from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';

import { Button, ButtonContainer, ModalFooter } from '@emburse/ui-core';

import { CardTransactionRow } from '@ReactComponents/CardTransactionRow';
import { ManualUploadCardProgramPanelKeys } from '@ReactComponents/ManualUploadCardProgramModal';
import { MultiSelectList } from '@ReactComponents/MultiSelectList';
import { IWizardPanelProps, WizardPanel } from '@ReactComponents/Wizard/Wizard';

import { SetDiffsToApply } from '@Actions/index';
import { CloseModal } from '@Actions/modalActions';
import { store } from '@ReactStore/store';
import { IAppStateContainer } from '@Reducers/index';

import { BulkUploadApiService as bulkUploadApiService } from '@ClientApi/bulkUpload';
import { BulkUploadType } from '@Shared/bulk_upload';
import { IAddCardDiff, IAddTransactionDiff } from '@Shared/card_statement_upload';
import { ILinkedAccountModel, IUserModel } from '@Shared/models';

// Styles
import * as style from './ManualUploadCardProgramSummary.styl';

interface ITransactionDiffWithCardHolder extends IAddTransactionDiff {
  cardHolder?: IUserModel;
}

interface IManualUploadCardProgramSummaryProps {
  closeModal: () => void;
  onFailure: (message?: string, doNotRefresh?: boolean) => void;
  onSuccess: () => void;
  setDiffsToApply: (diffs: IAddTransactionDiff[]) => void;
  userId?: string;
  addCardDiffs: IAddCardDiff[];
  addTransactionDiffs: IAddTransactionDiff[];
  allDiffs: (IAddCardDiff | IAddTransactionDiff)[];
  users: IUserModel[];
  cardProgram: ILinkedAccountModel;
  hasReviewedNewCards: boolean;
  hasReviewedDuplicates: boolean;
  savedFile: string;
  fileType: 'csv' | 'ofx';
  uploadType: BulkUploadType;
}

interface IManualUploadCardProgramSummaryState {
  programName: string;
}

function renderCardTransactionRowForDiff(diff: ITransactionDiffWithCardHolder) {
  return (
    <CardTransactionRow
      avatarUrl={diff.cardHolder && diff.cardHolder.avatar_url}
      cardHolderName={diff.cardHolder && diff.cardHolder.name}
      transactionId={diff.id}
      amount={diff.amount}
      merchant={diff.vendor}
      dateMilliseconds={diff.dateMilliseconds}
    />
  );
}

export class ManualUploadCardProgramSummary extends WizardPanel<
  ManualUploadCardProgramPanelKeys,
  IManualUploadCardProgramSummaryProps,
  IManualUploadCardProgramSummaryState
> {
  constructor(props: IWizardPanelProps<ManualUploadCardProgramPanelKeys> & IManualUploadCardProgramSummaryProps) {
    super(props);
  }

  async applyDiffs() {
    const me = this;
    const diffs = this.props.allDiffs;
    const savedFile = this.props.savedFile;
    const fileType = this.props.fileType;
    const account = this.props.cardProgram;
    const accountId = (account && account.id) || null;

    try {
      const res = await bulkUploadApiService.applyCardStatementDiffs(diffs, savedFile, fileType, accountId);
      if (res.message !== 'Success!') {
        me.props.onFailure(res.message, true);
        me.props.closeModal();
      } else {
        me.props.onSuccess();
        me.props.closeModal();
      }
    } catch (err) {
      me.props.onFailure();
      me.props.closeModal();
    }
  }

  componentDidMount() {
    const { addCardDiffs, hasReviewedNewCards } = this.props;

    if (addCardDiffs.length > 0 && !hasReviewedNewCards) {
      return this.props.changePanel('card_mapping');
    }
  }

  render() {
    const { addCardDiffs, addTransactionDiffs } = this.props;

    const usersById = store.getState().users.usersById;

    const transactionsWithNewCardHolderInfo = addTransactionDiffs.map(function (txDiff) {
      if (txDiff.cardHolder) {
        // if pre-populated with an existing cardholder, we're done!
        return txDiff;
      }
      const card = addCardDiffs.find(function (cardDiff) {
        return txDiff.accountId === cardDiff.id;
      });
      return {
        ...txDiff,
        cardHolder: card ? usersById[card.userId] : null
      };
    });

    const transactionDiffsToUpload = addTransactionDiffs.filter((txDiff) => !txDiff.skip);

    const numberToUpload = transactionDiffsToUpload.length;
    const { whitelabelConfiguration } = window;
    const manualStatementUploadLink = whitelabelConfiguration.supportLinks.manualStatementUpload;

    return (
      <div className={style.global}>
        <div>
          <p>Please select which transactions you wish to upload.</p>
          <div>
            <MultiSelectList<ITransactionDiffWithCardHolder>
              headerText={`${numberToUpload} Transactions`}
              items={transactionsWithNewCardHolderInfo}
              getItemSelected={(diff) => !diff.skip}
              getItemComponent={renderCardTransactionRowForDiff}
              onChange={(changes) => this.props.setDiffsToApply(changes)}
            />
          </div>
          <div>
            <a target="_blank" rel="noopener noreferrer" href={manualStatementUploadLink}>
              This transaction info does not look correct
            </a>
          </div>
        </div>
        <ModalFooter>
          <ButtonContainer alignButtons="right">
            <Button variant="outlined" onClick={this.props.goBack}>
              Back
            </Button>
            <Button primary onClick={() => this.applyDiffs()}>
              Import {numberToUpload} Transactions
            </Button>
          </ButtonContainer>
        </ModalFooter>
      </div>
    );
  }
}

// Connected Component

const mapStateToProps = (state: IAppStateContainer, parentProps: IWizardPanelProps<ManualUploadCardProgramPanelKeys>) => {
  const s = state.cardStatementUpload;
  const users = state.users.users;
  return {
    ...parentProps,
    users,
    error: s.error,
    allDiffs: s.allDiffs,
    addCardDiffs: s.addCardDiffs,
    addTransactionDiffs: s.addTransactionDiffs,
    errorDiffs: s.errorDiffs,
    hasReviewedNewCards: s.reviewedCardHolders,
    hasReviewedDuplicates: s.reviewedDuplicates,
    savedFile: s.savedFile,
    fileType: s.fileType
  };
};

const mapDispatchToProps = (dispatch: (action: AnyAction) => void) => ({
  closeModal: () => {
    dispatch(new CloseModal());
  },
  setDiffsToApply: (diffs: IAddTransactionDiff[]) => dispatch(new SetDiffsToApply(diffs))
});

export const ConnectedManualUploadCardProgramSummary = connect(mapStateToProps, mapDispatchToProps)(ManualUploadCardProgramSummary);
